import { create } from 'zustand';
import type { ConnectionState } from '../types';
import * as api from '../lib/api';

type OutputMode = 'normalized' | 'legacy';
type OutputDeclaration = { user_id: string; provider_output_mode?: OutputMode };

interface PresenceState {
  onlineUserIds: Record<string, boolean>;
  connectionState: ConnectionState;
  providerOutputModes: Record<string, 'normalized' | 'legacy' | null>;
  setProviderOutputMode: (userId: string, mode: 'normalized' | 'legacy' | null) => void;
  providerOutputEpoch: number;
  providerOutputRevisions: Record<string, number>;
  prepareProviderOutputSnapshot: () => (members: OutputDeclaration[]) => void;

  setOnlineUsers: (userIds: string[]) => void;
  userOnline: (userId: string) => void;
  userOffline: (userId: string) => void;
  setConnectionState: (state: ConnectionState) => void;
  reset: () => void;

  loadOnlineUsers: () => Promise<void>;
}

let outputRequestSequence = 0;
let outputSnapshotGeneration = 0;
const appliedOutputRequestByUser = new Map<string, number>();

export const usePresenceStore = create<PresenceState>()((set, get) => ({
  onlineUserIds: {},
  providerOutputModes: {},
  providerOutputEpoch: 0,
  providerOutputRevisions: {},
  setProviderOutputMode: (userId, mode) => set(state => ({
    providerOutputModes: { ...state.providerOutputModes, [userId]: mode },
    providerOutputRevisions: { ...state.providerOutputRevisions, [userId]: (state.providerOutputRevisions[userId] ?? 0) + 1 },
  })),
  prepareProviderOutputSnapshot: () => {
    const { providerOutputEpoch: epoch, providerOutputRevisions: revisions } = get();
    const generation = outputSnapshotGeneration;
    const request = ++outputRequestSequence;
    return members => set(state => {
      if (generation !== outputSnapshotGeneration || epoch !== state.providerOutputEpoch) return state;
      const modes = { ...state.providerOutputModes };
      for (const member of members) {
        if (member.provider_output_mode === undefined) continue;
        if (state.providerOutputRevisions[member.user_id] !== revisions[member.user_id] || request < (appliedOutputRequestByUser.get(member.user_id) ?? 0)) continue;
        appliedOutputRequestByUser.set(member.user_id, request);
        modes[member.user_id] = member.provider_output_mode ?? null;
      }
      return { providerOutputModes: modes };
    });
  },
  connectionState: 'disconnected',

  setOnlineUsers: (userIds) => set({ onlineUserIds: Object.fromEntries(userIds.map(id => [id, true])) }),

  userOnline: (userId) => set((s) => {
    const next = { ...s.onlineUserIds };
    next[userId] = true;
    return { onlineUserIds: next };
  }),

  userOffline: (userId) => set((s) => {
    const next = { ...s.onlineUserIds };
    delete next[userId];
    return { onlineUserIds: next };
  }),

  setConnectionState: (connectionState) => set(state => ({ connectionState, ...(connectionState === 'connected' ? {} : { providerOutputEpoch: state.providerOutputEpoch + 1, providerOutputModes: Object.fromEntries(Object.keys(state.providerOutputModes).map(id => [id, null])) }) })),

  loadOnlineUsers: async () => {
    try {
      const userIds = await api.fetchOnlineUsers();
      set({ onlineUserIds: Object.fromEntries(userIds.map(id => [id, true])) });
    } catch {
      // Ignore
    }
  },

  reset: () => {
    outputSnapshotGeneration++; appliedOutputRequestByUser.clear();
    set({ onlineUserIds: {}, providerOutputModes: {}, providerOutputEpoch: 0, providerOutputRevisions: {}, connectionState: 'disconnected' });
  },
}));
